import { useContext, useEffect, useState } from "react";
import UUID from "../../../Utilities/Generic/UUID";
import {
    type ColumnComponentType,
    type ComponentStyles,
    type ComponentType,
    DRAG_DROP_ROOT_CONTAINER_ID,
    type DragDropEditItemDefinition,
    DragDropEditorContext,
    type InternalColumnComponentType,
    isColumnComponentType,
    isInternalColumnComponentType,
} from "./DragDropTypes";

type DragDropCallback = () => void;

export class DragDropManager {
    protected components = new Map<string, ComponentType>();
    protected containers = new Map<string, Array<string>>();

    protected listeners = new Map<string, Set<DragDropCallback>>();

    constructor(components: Array<ComponentType>) {
        this.unwrapIntoContainer(components, DRAG_DROP_ROOT_CONTAINER_ID);
    }

    /**
     * Get the component using the component's id
     * @param id
     */
    public getComponent(id: string): ComponentType | undefined {
        return this.components.get(id);
    }

    /**
     * Get the list of component ids in the container
     * @param id
     */
    public getContainer(id: string): Array<string> {
        return this.containers.get(id) ?? [];
    }

    /**
     * Gets a list of components that match the provided type value
     * @param type
     */
    public getComponents(type: string): Array<ComponentType> {
        const results: Array<ComponentType> = [];

        for(const component of this.components.values()) {
            if(component.type !== type) {
                continue;
            }

            results.push(component);
        }

        return results;
    }

    /**
     * Whether a component of the provided type exists
     * @param type
     */
    public hasComponentType(type: string): boolean {
        for(const component of this.components.values()) {
            if(component.type === type) {
                return true;
            }
        }

        return false;
    }

    /**
     * Add listener that subscribes to the updates of a single container or component (if an id is provided), or
     * an update that occurs in the drag drop manager (if "update" is provided)
     * @param id
     * @param callback
     */
    public addListener(id: "update" | string, callback: DragDropCallback): void {
        let listeners = this.listeners.get(id);

        if(!listeners) {
            listeners = new Set<DragDropCallback>();
            this.listeners.set(id, listeners);
        }

        listeners.add(callback);
    }

    /**
     * Removes the subscribed listener
     * @param id
     * @param callback
     */
    public removeListener(id: "update" | string, callback: DragDropCallback): void {
        const listeners = this.listeners.get(id);

        if(!listeners) {
            return;
        }

        listeners.delete(callback);
    }

    /**
     * Delete all the listeners for the provided id (or "update")
     * @param id
     * @protected
     */
    protected deleteListeners(id: "update" | string): void {
        this.listeners.delete(id);
    }

    /**
     * Calls all the subscribed listeners for a given id
     * @param id
     * @protected
     */
    protected callListeners(id: "update" | string): void {
        const listeners = this.listeners.get(id);

        if(!listeners) {
            return;
        }

        for(const callback of listeners) {
            callback();
        }
    }

    /**
     * Unwraps a list of components into a provided container
     * @param components
     * @param rootId
     */
    public unwrapIntoContainer(components: Array<ComponentType>, rootId: string): void {
        const toResolve: Array<[ string, Array<ComponentType> ]> = [];
        const resolveLayer = (layerId: string, components: Array<ComponentType>): void => {
            let layer = this.containers.get(layerId);

            if(!layer) {
                layer = [];
                this.containers.set(layerId, layer);
            }

            for(const component of components) {
                const id = UUID.generate();

                layer.push(id);

                if(isColumnComponentType(component)) {
                    const value = [{
                        id   : this.createContainer(),
                        width: component.value[0].width,
                    }, {
                        id   : this.createContainer(),
                        width: component.value[1].width,
                    }];

                    toResolve.push([ value[0].id, component.value[0].components ]);
                    toResolve.push([ value[1].id, component.value[1].components ]);

                    this.components.set(id, {
                        type : "internal_column",
                        style: component.style,
                        value,
                    } as InternalColumnComponentType);
                } else {
                    this.components.set(id, component);
                }
            }
        };

        resolveLayer(rootId, components);

        let resolving = toResolve.shift();

        while(resolving) {
            resolveLayer(...resolving);

            resolving = toResolve.shift();
        }
    }

    /**
     * Creates a new container and returns the new id
     * @param components list of component ids to add to the container
     */
    public createContainer(components: Array<string> = []): string {
        const id = UUID.generate();

        this.containers.set(id, components);

        return id;
    }

    /**
     * Registers the component in the drag drop manager returning the registered id
     * @param component
     */
    public registerComponent(component: ComponentType): string {
        const id = UUID.generate();

        this.components.set(id, component);
        this.callListeners("update");

        return id;
    }

    /**
     * Removes the component at the given index in a given container and returns the component id, returns null if no
     * component is found
     * @param container
     * @param index
     */
    public detachComponent(container: string, index: number): string | null {
        const components = this.getContainer(container);

        if(index < 0 || index >= components.length) {
            return null;
        }

        const removed = components.splice(index, 1);

        if(!removed.length) {
            return null;
        }

        this.callListeners(container);
        this.callListeners("update");

        return removed[0];
    }

    /**
     * Adds the provided component id to the container ad the given index
     * @param container
     * @param id
     * @param index
     */
    public attachComponent(container: string, id: string, index: number): boolean {
        const components = this.containers.get(container);

        if(!components) {
            return false;
        }

        components.splice(index, 0, id);

        this.callListeners(container);
        this.callListeners("update");

        return true;
    }

    /**
     * Deletes a component and all components that are children of that components
     * @param container
     * @param index
     * @param items
     */
    public deleteComponent(
        container: string,
        index: number,
        items: Record<string, DragDropEditItemDefinition>
    ): void {
        const component = this.detachComponent(container, index);

        if(!component) {
            return;
        }

        // Handle components with containers
        const toDelete: Array<string> = [ component ];

        while(toDelete.length) {
            const working = toDelete.shift() as string;

            const component = this.components.get(working);

            if(!component) {
                continue;
            }

            const item = items[component.type];

            if(item) {
                if(typeof item.getContainers === "function") {
                    // Get the ids of the containers that the component contains
                    const containers = item.getContainers(component);

                    for(const id of containers) {
                        const container = this.getContainer(id);

                        // Add children components to be deleted
                        toDelete.push(...container);

                        // Delete containers
                        this.containers.delete(id);
                        this.deleteListeners(id);
                    }
                }
            }

            this.components.delete(working);
            this.deleteListeners(working);
        }

        this.callListeners("update");
    }

    /**
     * Updates the value of the component
     * @param id
     * @param value
     */
    public updateComponent<C extends ComponentType>(id: string, value: C["value"]): void {
        const component = this.components.get(id);

        if(!component) {
            return;
        }

        component.value = value;

        this.callListeners(id);
        this.callListeners("update");
    }

    /**
     * Updates the styles of the component
     * @param id
     * @param key
     * @param value
     */
    public updateStyle<Key extends keyof ComponentStyles>(id: string, key: Key, value: ComponentStyles[Key]): void {
        const component = this.components.get(id);

        if(!component) {
            return;
        }

        component.style[key] = value;

        this.callListeners(id);
        this.callListeners("update");
    }

    /**
     * Rebuilds the component tree into its nested form based on the provided root id.
     * If root id is not provided the default root container will be resolved
     * @param rootId
     */
    public toArray(rootId = DRAG_DROP_ROOT_CONTAINER_ID): Array<ComponentType> {
        const results: Array<ComponentType> = [];

        const toResolve: Array<[ string, Array<ComponentType> ]> = [[
            rootId,
            results,
        ]];

        let resolving = toResolve.shift();

        while(resolving) {
            const container = this.containers.get(resolving[0]);

            if(!container) {
                resolving = toResolve.shift();
                continue;
            }

            for(const id of container) {
                const component = this.components.get(id);

                if(!component) {
                    continue;
                }

                if(isInternalColumnComponentType(component)) {
                    const columns: ColumnComponentType = {
                        type : "column",
                        style: component.style as ColumnComponentType["style"],
                        value: [{
                            width     : component.value[0].width,
                            components: [],
                        }, {
                            width     : component.value[1].width,
                            components: [],
                        }],
                    };

                    toResolve.push([ component.value[0].id, columns.value[0].components ]);
                    toResolve.push([ component.value[1].id, columns.value[1].components ]);

                    resolving[1].push(columns);
                } else {
                    resolving[1].push(component);
                }
            }

            resolving = toResolve.shift();
        }

        return results;
    }
}

export function useDragDropContainer(id: string): Array<string> {
    const { manager } = useContext(DragDropEditorContext);

    const [ components, setComponents ] = useState<Array<string>>([]);

    useEffect(() => {
        const listener = () => {
            setComponents([ ...manager.getContainer(id) ]);
        };

        manager.addListener(id, listener);
        listener();

        return () => {
            manager.removeListener(id, listener);
        };
    }, [ manager, id ]);

    return components;
}

export function useDragDropComponent(id: string): ComponentType | null {
    const { manager } = useContext(DragDropEditorContext);

    const [ component, setComponent ] = useState<null | ComponentType>(null);

    useEffect(() => {
        const listener = () => {
            const component = manager.getComponent(id);

            setComponent(component ? { ...component } : null);
        };

        manager.addListener(id, listener);
        listener();

        return () => {
            manager.removeListener(id, listener);
        };
    }, [ manager, id ]);

    return component;
}
